import { Link } from "react-router-dom";
import { agentsHref } from "@/lib/atwAgentsHost";

export function WikiPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Agent wiki</h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
          How to set up the three agents you sell first, and how they use the knowledge base.
        </p>
      </div>

      <section className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">An agent</h2>
        <p>
          An agent is the worker. You hire as many as you need, give each one a human owner, and
          choose its job. Hire as many as the work needs. Usage comes from the shared credit pool.
        </p>
      </section>

      <section className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Three agents to start</h2>
        <p>They run in order. Each one does one job and then hands the lead on.</p>
        <ol className="list-decimal space-y-2 pl-5">
          <li>
            <span className="font-medium text-gray-900 dark:text-white">Lead.</span> Finds people
            or companies who might buy and records the lead. It does not enrich and it does not sell.
          </li>
          <li>
            <span className="font-medium text-gray-900 dark:text-white">Enrich.</span> Adds role,
            website, and industry only when it can cite a source. It does not email the buyer.
          </li>
          <li>
            <span className="font-medium text-gray-900 dark:text-white">Sales.</span> Replies to
            ready leads, updates the CRM, and asks the owner before a price, a discount, or a date
            that is not already written down.
          </li>
        </ol>
      </section>

      <section className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Knowledge</h2>
        <p>
          The company has one knowledge base, shared with One. Hierarchy is optional. You can create
          articles with no book, and books do not need a custom library.
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <span className="font-medium text-gray-900 dark:text-white">Knowledge base:</span> everything
            the company allows agents to know.
          </li>
          <li>
            <span className="font-medium text-gray-900 dark:text-white">Library:</span> an optional
            shelf that groups books (Suite, or Other for custom keys).
          </li>
          <li>
            <span className="font-medium text-gray-900 dark:text-white">Book:</span> an optional
            subject grouping (one collection key on the article).
          </li>
          <li>
            <span className="font-medium text-gray-900 dark:text-white">Article:</span> one markdown
            page. It may stand alone or sit in one or more books.
          </li>
        </ul>
        <p>
          When you assign later on the agent, a library includes every book and article on it,
          including pages you add later. A book includes its articles. One article gives only that
          page. An exclusion can block a single article inside something already assigned.
        </p>
        <p>
          <Link to={agentsHref("/knowledge")} className="underline underline-offset-2">
            Open the knowledge base
          </Link>
          {" · "}
          <Link to={agentsHref("/knowledge/articles")} className="underline underline-offset-2">
            All articles
          </Link>
          .
        </p>
      </section>

      <section className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">What you give an agent</h2>
        <p>
          A library is not enough on its own. Each agent also needs a mailbox it sends from, a CRM
          user it writes as, a human owner, and standing orders: who it works, what it may promise,
          and when it must stop.
        </p>
      </section>

      <section className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tools</h2>
        <ul className="list-disc space-y-1 pl-5">
          <li>Mail: read, reply, and send as that agent.</li>
          <li>CRM: create and update the lead, then record the next step.</li>
          <li>Knowledge: answer only from the articles that agent is assigned.</li>
          <li>Calendar: see what is booked and offer a time the owner already allowed.</li>
          <li>Documents: read a one-pager and attach a file the owner approved.</li>
          <li>MCP and API: systems outside ATW that you connect.</li>
          <li>Browser: sites with no API. Off unless you turn it on for that agent.</li>
          <li>Approval: draft for the owner, or stop and ask.</li>
          <li>Notify: tell the owner when a person has to decide.</li>
        </ul>
      </section>

      <section className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Schedules</h2>
        <p>
          A schedule is a named clock the organization creates once and assigns to many agents. It
          is not owned by one agent, and it does not decide the job. The lead and enrich agents can
          share a short poll, about every 5 minutes. The sales agent can use that same poll for new
          mail, plus a morning pass for follow-ups that are due.
        </p>
        <p>
          Between runs, open work stays in a queue: new lead, waiting on enrichment, ready for
          sales, waiting on the owner. The next run picks up the queue. It does not reread the
          whole mailbox when nothing changed.
        </p>
      </section>

      <section className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Browser access</h2>
        <p>
          Browser access is a setting on the agent. It stays off. Turn it on only when a step has
          no mail, CRM, or API, and limit it to the sites you name. Every visit belongs on that
          agent&apos;s timeline.
        </p>
      </section>

      <section className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Overseer</h2>
        <p>
          Overseer is where a run stops for a person: the agent is unsure, the buyer asks for a
          discount, or a login wall blocks the browser. The owner resolves it. The agent continues
          from that note on the next run.
        </p>
      </section>

      <section className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">What you do with a client</h2>
        <ol className="list-decimal space-y-2 pl-5">
          <li>Hire three agents: Lead, Enrich, and Sales.</li>
          <li>
            Open Knowledge and write their products and public price on Products and public price.
          </li>
          <li>
            Write the floor price on Floor price and discount authority, and leave that article
            excluded from the sales agent.
          </li>
          <li>Connect mail and CRM when those connectors are on. Browser stays off.</li>
        </ol>
      </section>
    </div>
  );
}
